import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import type { QRCode, StoryItem, ViewerState } from "./types";

// Resolves the viewer state for a QR code id. Two reads:
//   1. qrCodes/{id} — tells us the lifecycle stage and links to a story.
//   2. stories/{storyId} — only fetched if the qrCode is .draft or .active.
//
// Both reads are anonymous; Firestore Security Rules permit:
//   • qrCodes/* full public read
//   • stories/* read only when isDraft == false
// So a draft story will return a permission-denied error from the Firestore
// SDK — we treat that as the "draft, not yet public" state.

export async function loadStoryForCode(code: string): Promise<ViewerState> {
  if (!code || !isValidCode(code)) {
    return { kind: "missing", code };
  }

  // 1. QR lookup.
  let qrSnap;
  try {
    qrSnap = await getDoc(doc(db, "qrCodes", code));
  } catch (err) {
    return errorState(err, "Couldn't reach the server. Check your connection and try again.");
  }
  if (!qrSnap.exists()) {
    return { kind: "missing", code };
  }
  const qr = qrSnap.data() as QRCode;

  // 2. Branch by lifecycle.
  switch (qr.status) {
    case "active": {
      if (!qr.storyItemId) return { kind: "unassigned", code };
      return await loadStory(qr.storyItemId, code);
    }
    case "draft": {
      // The story exists but is private — show a "still being written" state
      // even if we have permission to fetch it (which we wouldn't anonymously).
      return { kind: "draft", code };
    }
    case "unassigned":
    case "generated":
    case "shipped":
    case "deactivated":
    default:
      return { kind: "unassigned", code };
  }
}

async function loadStory(storyId: string, code: string): Promise<ViewerState> {
  let snap;
  try {
    snap = await getDoc(doc(db, "stories", storyId));
  } catch (err) {
    // We only reach here for an ACTIVE qr (drafts are caught earlier by status),
    // so a permission-denied read means the story is restricted to the owner's
    // Circle or kept private — the rules forbid anonymous access. Show the
    // gentle "private" state rather than leaking that it exists as a draft.
    if (isPermissionDenied(err)) return { kind: "private" };
    return errorState(err, "Couldn't load this story. Try again in a moment.");
  }
  if (!snap.exists()) return { kind: "unassigned", code };

  const story = { id: snap.id, ...(snap.data() as Omit<StoryItem, "id">) };
  if (story.isDraft) return { kind: "draft", code };
  // Axis A — only publicly-scannable stories are shown in the anonymous viewer.
  // Stories the owner has turned off for scanning (publiclyScannable === false,
  // or legacy circle/private) are never shown here. Defense in depth — the
  // rules would normally have already denied the read above.
  if (!isPubliclyScannable(story)) {
    return { kind: "private" };
  }
  // Password-gated public stories — return the doc so the client can verify
  // locally with PBKDF2. The plain-text password is never transmitted.
  if (story.isPrivate) return { kind: "locked", story };
  return { kind: "story", story };
}

// Axis A (public scan visibility), mirroring `publiclyScannableOf` in the iOS
// app and `firestore.rules`. New docs carry an explicit `publiclyScannable`
// boolean. Un-migrated docs derive it from the legacy single `audience` field:
// only "anyone" (or no audience at all, on the very oldest docs) was ever
// publicly scannable — legacy "circle"/"private" map to false.
function isPubliclyScannable(story: StoryItem): boolean {
  if (typeof story.publiclyScannable === "boolean") {
    return story.publiclyScannable;
  }
  const audience = story.audience ?? "anyone";
  return audience === "anyone";
}

// Same character class the iOS scanner accepts. Anything else means a hand-
// typed URL or a tampered link — render the "unknown stamp" state rather than
// firing a Firestore read for a nonsense doc id.
function isValidCode(code: string): boolean {
  return /^[A-Za-z0-9_-]{6,64}$/.test(code);
}

function isPermissionDenied(err: unknown): boolean {
  return (
    typeof err === "object" &&
    err !== null &&
    "code" in err &&
    (err as { code: string }).code === "permission-denied"
  );
}

function errorState(err: unknown, fallback: string): ViewerState {
  console.error("[storyviewer] firestore error:", err);
  return { kind: "error", message: fallback };
}
